// SchoolConfigPage.js
import React, { useState, useEffect } from 'react';
import { FormGroup, Input, Label, Button, Alert, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import API from '../../services/API';

const configFields = [
    { key: 'early_cutoff', label: 'Early Attendance Cutoff (e.g. 07:30)', type: 'time', style: { maxWidth: 200 } },
];

const SchoolConfigPage = () => {
    const [config, setConfig] = useState({
        early_cutoff: '07:30',
        late_after: '07:45',
        grace_period: 5,
        max_late: 15,
        custom_message: '',
        mc_category: '',
        mc_sub_category: '',
    });
    const [loading, setLoading] = useState(true);
    const [savingField, setSavingField] = useState(null);
    const [error, setError] = useState(null);

    const [mcReasons, setMCReasons] = useState([]);
    const [loadingMC, setLoadingMC] = useState(true);
    const [mcError, setMCError] = useState(null);
    const [selectedMC, setSelectedMC] = useState('');

    useEffect(() => {
        fetchConfig();
        getMCReasons();
        fetchDefaultMC();
    }, []);

    // Fetch config
    const fetchConfig = async () => {
        setLoading(true);
        setError(null);
        try {
            const headers = { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') };
            const res = await API.get('/portal/attendance/config', { headers });
            if (res.data.response_code === 1000) {
                setConfig(res.data.data);
                // Set selectedMC if values are already saved
                if (res.data.data.mc_category) {
                    setSelectedMC(`${res.data.data.mc_category}|||${res.data.data.mc_sub_category || ''}`);
                }
            } else {
                setError(res.data.message);
            }
        } catch (err) {
            setError('Failed to load config');
        } finally {
            setLoading(false);
        }
    };

    // Fetch MC Reasons from external endpoint
    const getMCReasons = async () => {
        setLoadingMC(true);
        setMCError(null);
        try {
            const url = "https://api.schoolpay.tech/attendance/absenceReason";
            const response = await fetch(url, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });
            const responseJSON = await response.json();
            if (responseJSON.response_code === 1000) {
                setMCReasons(responseJSON.data);
            } else {
                setMCError(responseJSON.message);
            }
        } catch (error) {
            setMCError('Failed to load MC reasons');
        } finally {
            setLoadingMC(false);
        }
    };
    // Fetch Default MC
    const fetchDefaultMC = async () => {
        try {
            const headers = { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') };
            const res = await API.get('/portal/attendance/mcConfig', { headers });
            if (res.data.response_code === 1000) {
                setConfig(cfg => ({
                    ...cfg,
                    mc_category: res.data.data.category || '',
                    mc_sub_category: res.data.data.sub_category || '',
                }));
                if (res.data.data.category) {
                    setSelectedMC(`${res.data.data.category}|||${res.data.data.sub_category || ''}`);
                }
            }
        } catch (err) {
            setError('Failed to load MC default');
        }
    };
    // Save config field
    const saveField = async (field) => {
        setSavingField(field);
        setError(null);
        try {
            const headers = { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') };
            const body = { updated_field: field, [field]: config[field] };
            const res = await API.post('/portal/attendance/config', body, { headers });
            if (res.data.response_code === 1000) {
                toast.success(`${configFields.find(f => f.key === field)?.label || field} saved!`, { autoClose: 2000 });
                fetchConfig();
            } else {
                setError(res.data.message);
            }
        } catch (err) {
            setError('Failed to save config');
        } finally {
            setSavingField(null);
        }
    };

    // Save selected MC reason as default
    const saveDefaultMC = async (e) => {
        e.preventDefault();
        if (!selectedMC) {
            toast.error("Please select a default MC Reason.");
            return;
        }
        const [mc_category, mc_sub_category] = selectedMC.split('|||');
        setSavingField('mc_reason');
        setError(null);
        try {
            const headers = { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') };
            const body = {
                category: mc_category,
                sub_category: mc_sub_category,
            };

            const res = await API.post('/portal/attendance/mcConfig', body, { headers });
            if (res.data.response_code === 1000) {
                toast.success('Default MC Reason saved!', { autoClose: 2000 });
                setConfig(cfg => ({ ...cfg, mc_category, mc_sub_category }));
            } else {
                setError(res.data.message);
            }
        } catch (err) {
            console.log(err)
            setError('Failed to save MC reason');
        } finally {
            setSavingField(null);
        }
    };

    return (
        <div
            className="container"
            style={{
                alignSelf: 'start',
                marginTop: 30,
                textAlign: 'left',
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 1px 12px rgba(0,0,0,0.08)",
                padding: 32,
            }}
        >
            <h3 style={{ textAlign: 'left' }}>Attendance Configuration</h3>
            {error && <Alert color="danger">{error}</Alert>}
            {loading ? (
                <div style={{ textAlign: 'center', marginTop: 60 }}><Spinner /></div>
            ) : (
                <form style={{ textAlign: 'left' }}>
                    {configFields.map(({ key, label, type, style, min }) => (
                        <FormGroup key={key} style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Label for={key} style={{ minWidth: 210 }}>{label}</Label>
                            <Input
                                type={type}
                                name={key}
                                id={key}
                                value={config[key]}
                                onChange={e => setConfig({ ...config, [key]: e.target.value })}
                                min={min}
                                required={key !== "custom_message"}
                                style={style}
                            />
                            <Button
                                color="primary"
                                size="sm"
                                style={{ marginLeft: 12 }}
                                disabled={savingField === key}
                                onClick={e => { e.preventDefault(); saveField(key); }}
                            >
                                {savingField === key ? "Saving..." : "Save"}
                            </Button>
                        </FormGroup>
                    ))}
                </form>
            )}

            {/* MC Reason Section */}
            <div style={{ marginTop: 40 }}>
                <h5>Default MC/Absent Reason</h5>
                {mcError && <Alert color="danger">{mcError}</Alert>}
                {loadingMC ? (
                    <Spinner size="sm" />
                ) : (
                    <form onSubmit={saveDefaultMC} style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                        <FormGroup style={{ minWidth: 280, marginBottom: 0 }}>
                            <Label for="default_mc_reason">Select Default Reason</Label>
                            <Input
                                type="select"
                                id="default_mc_reason"
                                value={selectedMC}
                                onChange={e => setSelectedMC(e.target.value)}
                            >
                                <option value="">-- Please select --</option>
                                {mcReasons.map(r => (
                                    <option key={r.reason_id} value={`${r.name}|||${r.sub_reason || ''}`}>
                                        {r.name}{r.sub_reason ? ` — ${r.sub_reason}` : ''}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <Button
                            color="success"
                            disabled={savingField === 'mc_reason'}
                            type="submit"
                        >
                            {savingField === 'mc_reason' ? "Saving..." : "Save Default"}
                        </Button>
                    </form>
                )}
                {config.mc_category && (
                    <div style={{ marginTop: 16, color: "#2a2", fontWeight: 500 }}>
                        Saved Default: <b>{config.mc_category}</b>{config.mc_sub_category ? ` — ${config.mc_sub_category}` : ''}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchoolConfigPage;
